import { DatePicker, DatePickerProps } from '@heroui/react'
import { type DateValue, parseDate } from '@internationalized/date'
import { Field, FieldProps } from 'formik'
import { motion } from 'framer-motion'

export interface DateFormikProps
  extends Omit<
    DatePickerProps,
    'value' | 'onChange' | 'minValue' | 'maxValue' | 'isDateUnavailable'
  > {
  label?: string
  classNameContainer?: string
  name: string
  placeholder?: string
  minValue?: Date | string
  maxValue?: Date | string
  isDateUnavailable?: (date: Date) => boolean
}

const DateFormik = ({
  label,
  classNameContainer,
  name,
  minValue,
  maxValue,
  isDateUnavailable,
  ...props
}: DateFormikProps) => {
  // Convert Date objects to CalendarDate format for HeroUI DatePicker
  const getCalendarDate = (date: Date | string | undefined) => {
    if (!date) return undefined
    const dateObj = typeof date === 'string' ? new Date(date) : date

    return parseDate(dateObj.toISOString().split('T')[0])
  }

  // Convert DateValue back to Date for isDateUnavailable callback
  const convertDateValueToDate = (dateValue: DateValue): Date => {
    // DateValue has year, month, day properties
    return new Date(dateValue.year, dateValue.month - 1, dateValue.day)
  }

  // Wrap isDateUnavailable to handle DateValue conversion
  const wrappedIsDateUnavailable = isDateUnavailable
    ? (dateValue: DateValue) => isDateUnavailable(convertDateValueToDate(dateValue))
    : undefined

  return (
    <Field name={name}>
      {({ field, meta, form }: FieldProps) => (
        <motion.div className={classNameContainer}>
          <DatePicker
            showMonthAndYearPickers
            className="h-full"
            errorMessage={meta.error && meta.touched ? meta.error : ''}
            isDateUnavailable={wrappedIsDateUnavailable}
            isInvalid={meta.error && meta.touched ? true : false}
            label={label}
            labelPlacement="outside"
            maxValue={getCalendarDate(maxValue)}
            minValue={getCalendarDate(minValue)}
            name={name}
            radius="sm"
            value={field.value ? parseDate(field.value) : null}
            variant="bordered"
            calendarProps={{
              classNames: {
                content: 'w-[290px]',
                base: 'w-[290px]',
              },
            }}
            classNames={{
              label: ['text-gray-900', 'dark:text-white'],
              input: ['text-gray-900', 'dark:text-white'],
              calendarContent: 'w-[290px]',
              calendar: 'w-[290px]',
              segment: ['!text-gray-900', 'dark:!text-white'],
              inputWrapper: [
                'text-gray-900',
                'dark:!text-white',
                'bg-gray-50',
                'dark:bg-[#1e1e20]',
                'border-gray-200',
                'dark:border-default-600',
                'hover:bg-gray-100',
                'dark:hover:bg-[#1e1e20]/70',
                'group-data-[focus=true]:bg-gray-100',
                'dark:group-data-[focus=true]:bg-[#1e1e20]/50',
                'group-data-[focus=true]:border-primary',
                'dark:group-data-[focus=true]:border-primary',
                '!cursor-text',
              ],
            }}
            onChange={date => {
              form.setFieldValue(field.name, date ? date.toString() : '')
            }}
            {...props}
          />
        </motion.div>
      )}
    </Field>
  )
}

export default DateFormik
